import React, { useEffect, useState } from "react";
import { API_URL, getCurrency } from "../services/api";
import { formatPrice } from "../utils/currency";
import { parseServerDate } from "../utils/time";
import { useT } from "../i18n/useT";

function relativeMinutes(createdAt) {
  if (!createdAt) return { label: "—", mins: 0 };
  const now = Date.now();
  const t = parseServerDate(createdAt).getTime();
  const mins = Math.max(0, Math.floor((now - t) / 60000));
  if (mins < 1) return { label: "just now", mins };
  if (mins < 60) return { label: `${mins}m`, mins };
  const h = Math.floor(mins / 60);
  return { label: `${h}h ${mins % 60}m`, mins };
}

/**
 * Role-aware next-step actions.
 *   chef:    advance cooking states (pending→preparing→ready)
 *   waiter:  serve / cancel states (ready→served)
 *   manager: can do anything
 */
function nextActions(role, status) {
  const chef = ["pending", "confirmed", "preparing", "ready"];
  const waiter = ["served"]; // waiters only run food: ready → served

  const transitions = {
    pending: ["preparing", "cancelled"],
    confirmed: ["preparing", "cancelled"],
    preparing: ["ready", "cancelled"],
    ready: ["served"],
    served: ["completed"],
    completed: [],
    cancelled: [],
  };

  let allowed = transitions[status] || [];
  if (role === "chef") allowed = allowed.filter((s) => chef.includes(s) || s === "cancelled");
  if (role === "waiter") allowed = allowed.filter((s) => waiter.includes(s) || s === "cancelled");
  return allowed;
}

// Maps next-state values to translation keys. The actual label is
// resolved via t() inside the component so a language switch in the
// navbar updates every Order card live.
const LABEL_KEY = {
  preparing: "btn_mark_preparing",
  ready:     "btn_mark_ready",
  served:    "btn_mark_served",
  completed: "btn_mark_completed",
  cancelled: "btn_cancel",
};

// Maps server-side status strings to translation keys. Used to render
// the badge in the corner of each Order card.
const STATUS_KEY = {
  pending:    "status_pending",
  preparing:  "status_preparing",
  ready:      "status_ready",
  served:     "status_served",
  cancelled:  "status_cancelled",
  completed:  "status_served",  // treat completed as served in display
  confirmed:  "status_pending",
};

const STYLE = {
  preparing: "btn",
  ready: "btn ok",
  served: "btn ok",
  completed: "btn secondary",
  cancelled: "btn danger",
};

// Lifecycle steps shown in the per-card status track. The keys match
// the server-side order_status values; the labelKey resolves through
// the dashboard i18n bundle so the track follows the navbar language
// switcher live.
const STATUS_STEPS = [
  { key: "pending",   labelKey: "status_pending" },
  { key: "preparing", labelKey: "status_preparing" },
  { key: "ready",     labelKey: "status_ready" },
  { key: "served",    labelKey: "status_served" },
];

// Map the actual order.status → index into STATUS_STEPS so we can light
// up the right dot. confirmed acts as pending; completed acts as served.
const STATUS_STEP_INDEX = {
  pending: 0,
  confirmed: 0,
  preparing: 1,
  ready: 2,
  served: 3,
  completed: 3,
};

/**
 * Display-only lifecycle progress shown at the top of each OrderCard.
 * Cancelled orders get a single red "Cancelled" bar instead of the
 * 4-step track. Status changes still happen via the existing action
 * buttons below — the dots are not tappable on purpose so a chef can't
 * misclick into the wrong state during a busy service.
 */
function StatusTrack({ status, t }) {
  if (status === "cancelled") {
    return (
      <div className="status-track-cancelled" role="status">
        ✕ {t("status_cancelled")}
      </div>
    );
  }
  const current = STATUS_STEP_INDEX[status] ?? 0;
  return (
    <div className="status-track" aria-label="Order progress">
      {STATUS_STEPS.map((step, i) => {
        const cls =
          i < current ? "status-step done"
          : i === current ? "status-step current"
          : "status-step todo";
        return (
          <React.Fragment key={step.key}>
            <div className={cls}>
              <span className="status-dot">
                {i < current ? "✓" : i + 1}
              </span>
              <span className="status-label">{t(step.labelKey)}</span>
            </div>
            {i < STATUS_STEPS.length - 1 && (
              <div className={"status-connector" + (i < current ? " done" : "")} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

const ITEM_NEXT = { pending: "preparing", preparing: "ready", ready: "served" };

export default function OrderCard({ order, role, onStatusChange, onItemStatusChange }) {
  const currency = getCurrency();
  // Keep the age badge fresh even between refreshes
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const { t } = useT();
  const age = relativeMinutes(order.created_at);
  // "just now" → translated; minute/hour labels stay numeric so they
  // remain glanceable across languages.
  const ageDisplay = age.mins < 1 ? t("just_now") : age.label;
  const ageClass =
    age.mins >= 20 ? "urgent" : age.mins >= 10 ? "warn" : "";

  const actions = nextActions(role || "manager", order.status);
  const statusKey = STATUS_KEY[order.status] || null;
  const statusLabel = statusKey ? t(statusKey) : order.status;

  return (
    <div className={`order-card status-${order.status}`}>
      <div className="space-between">
        <h3>
          {t("table")} {order.table_number ?? "—"}{order.area ? <span style={{ marginLeft: 6, fontSize: "0.68rem", background: "#eef2f7", color: "#1F3A5F", padding: "1px 7px", borderRadius: 10, fontWeight: 700 }}>{order.area}</span> : null}
          {/* Show the round-within-tab to staff -- "Round 2" matches how
              waiters communicate with guests. The global #orderId is
              still shown small for cross-reference with receipts and
              the database. */}
          {order.round_number ? (
            <>
              {" · "}
              <span style={{ color: "#008080" }}>
                {t("round")} {order.round_number}
              </span>
            </>
          ) : (
            <> · #{order.id}</>
          )}
          {order.round_number && (
            <span className="muted" style={{ fontSize: "0.75rem", marginLeft: 8, fontWeight: 400 }}>
              #{order.id}
            </span>
          )}
          <span className={`age-badge ${ageClass}`}>⏱ {ageDisplay}</span>
        </h3>
        <span className="muted" style={{ textTransform: "uppercase", fontSize: "0.75rem" }}>
          {statusLabel}
        </span>
      </div>

      <div className="meta">
        {parseServerDate(order.created_at).toLocaleTimeString()} · {formatPrice(order.total ?? 0, currency)}
        {order.waiter_name && (
          <>
            {" · "}
            <span style={{ color: "#008080", fontWeight: 600 }}>
              👤 {order.waiter_name}
            </span>
          </>
        )}
        {/* Waiter-placed orders carry placed_by_name. We only show this
            badge when it differs from waiter_name -- otherwise the same
            person would be tagged twice on the same line. */}
        {order.placed_by_name &&
          order.placed_by_name !== order.waiter_name && (
            <>
              {" · "}
              <span
                title="Order taken tableside by this staff member"
                style={{
                  background: "#fff8e7",
                  color: "#8a5a00",
                  padding: "1px 8px",
                  borderRadius: 999,
                  fontSize: "0.78rem",
                  fontWeight: 600,
                }}
              >
                👋 by {order.placed_by_name}
              </span>
            </>
          )}
        {/* If placed_by IS the same as waiter, show a smaller "tableside"
            chip so the kitchen still knows it wasn't a QR scan. */}
        {order.placed_by_name &&
          order.placed_by_name === order.waiter_name && (
            <>
              {" · "}
              <span
                title="Order taken at the table by the waiter"
                style={{
                  background: "#fff8e7",
                  color: "#8a5a00",
                  padding: "1px 8px",
                  borderRadius: 999,
                  fontSize: "0.78rem",
                  fontWeight: 600,
                }}
              >
                👋 tableside
              </span>
            </>
          )}
      </div>

      <StatusTrack status={order.status} t={t} />

      <ul>
        {order.items.map((it) => (
          <li key={it.id}>
            <div>
              <b>{it.quantity}×</b> {it.name || `#${it.menu_id}`}
              {it.price != null && (
                <span className="muted"> · {formatPrice(it.price * it.quantity, currency)}</span>
              )}
            </div>
            {it.modifiers && it.modifiers.length > 0 && (
              <div className="line-mods-display">
                {it.modifiers.map((m, i) => (
                  <span key={i} className="mod-tag">
                    {m.name}
                    {m.price > 0 && ` (+${formatPrice(m.price, currency)})`}
                  </span>
                ))}
              </div>
            )}
            {it.notes && (
              <div className="line-note-display">↳ {it.notes}</div>
            )}
            {onItemStatusChange && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
                <span className="muted" style={{ fontSize: "0.72rem", textTransform: "uppercase" }}>
                  {t(STATUS_KEY[it.status] || "status_pending")}
                </span>
                {ITEM_NEXT[it.status] && (role !== "waiter" || it.status === "ready") && (
                  <button
                    className="btn small"
                    onClick={() => onItemStatusChange(order.id, it.id, ITEM_NEXT[it.status])}
                  >
                    {t(LABEL_KEY[ITEM_NEXT[it.status]] || ITEM_NEXT[it.status])}
                  </button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {order.notes && (
        <div className="notice" style={{ marginTop: 8 }}>
          📝 {order.notes}
        </div>
      )}

      <div className="actions">
        {!onItemStatusChange && actions.map((s) => (
          <button
            key={s}
            className={(STYLE[s] || "btn") + " small"}
            onClick={() => onStatusChange(order.id, s)}
          >
            {LABEL_KEY[s] ? t(LABEL_KEY[s]) : s}
          </button>
        ))}
        <a
          className="btn secondary small"
          href={`${API_URL.replace(/\/$/, "")}/public/receipt/${order.public_token}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          🧾 {t("receipt")}
        </a>
      </div>
    </div>
  );
}
